import React, { useState, useEffect } from "react";
import { getApi } from "../../../Services/apiService";
import {
  formatMessageTime,
  getOtherParticipantId,
  getMessagesPaginated,
  setChatAsAdminSupport,
  getIndividualChatBlocksMap,
  resolveMyParticipantIdInChat,
  unblockUserInChat,
  deleteChat,
} from "../../../Services/messagingService";
import { toast } from "react-toastify";
import { renderColorDotAndName } from "../../../Utils/colors";
import { stripMessageMarkup } from "../../../Utils/messageFormatting";
import { collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../../../Services/firestore";
import { getFamilyTreeIdFromStorage, getAdminDetailsFromStorage, getAdminUserIdFromDetails } from "../../../Utils/headOfLineRole";
import { formatDashboardFamilyLineHeadName } from "../../../Utils/dashboardMemberDisplayName";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleLeft } from "@fortawesome/free-solid-svg-icons";

const HIDDEN_CHATS_KEY = (userId) => `messages_hidden_chats_${userId || "default"}`;
const ARCHIVED_CHATS_KEY = (userId) => `messages_archived_chats_${userId || "default"}`;

const ChatList = ({ currentUserId, chats = [], chatsLoading = false, onChatSelect, selectedChatId, onCollapseChatList }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [memberInfoMap, setMemberInfoMap] = useState({});
  const [participantNamesFromMessages, setParticipantNamesFromMessages] = useState({}); // Cache names from messages
  const [fetchingMessageNames, setFetchingMessageNames] = useState(new Set()); // Track which chats we're fetching names for
  const [showNewChatTooltip, setShowNewChatTooltip] = useState(false);
  const [showFullList, setShowFullList] = useState(false);
  const [hiddenChatIds, setHiddenChatIds] = useState(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_CHATS_KEY(currentUserId));
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [archivedChatIds, setArchivedChatIds] = useState(() => {
    try {
      const raw = localStorage.getItem(ARCHIVED_CHATS_KEY(currentUserId));
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [chatMenuOpenId, setChatMenuOpenId] = useState(null);
  const [deletingChatId, setDeletingChatId] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_CHATS_KEY(currentUserId));
      setHiddenChatIds(raw ? JSON.parse(raw) : []);
    } catch {
      setHiddenChatIds([]);
    }
    try {
      const ar = localStorage.getItem(ARCHIVED_CHATS_KEY(currentUserId));
      setArchivedChatIds(ar ? JSON.parse(ar) : []);
    } catch {
      setArchivedChatIds([]);
    }
  }, [currentUserId]);

  const handleUnblockFromList = async (chat) => {
    if (!chat || !currentUserId) return;
    const me = resolveMyParticipantIdInChat(chat, currentUserId);
    const other = getOtherParticipantId(chat, me);
    if (!other) {
      toast.error("Unable to unblock.");
      return;
    }
    setChatMenuOpenId(null);
    try {
      await unblockUserInChat(chat.id, me, other);
      toast.success("User unblocked.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to unblock user.");
    }
  };

  const removeChatFromLocalPreferences = (chatId) => {
    const id = String(chatId);
    setHiddenChatIds((prev) => {
      const next = prev.filter((x) => String(x) !== id);
      try {
        localStorage.setItem(HIDDEN_CHATS_KEY(currentUserId), JSON.stringify(next));
      } catch (e) {
        console.error("Failed to save hidden chats", e);
      }
      return next;
    });
    setArchivedChatIds((prev) => {
      const next = prev.filter((x) => String(x) !== id);
      try {
        localStorage.setItem(ARCHIVED_CHATS_KEY(currentUserId), JSON.stringify(next));
      } catch (e) {
        console.error("Failed to save archived chats", e);
      }
      return next;
    });
  };

  /** Permanently removes the chat document and all messages from Firestore. */
  const permanentlyDeleteChat = async (chatId) => {
    if (!chatId || deletingChatId) return;
    setDeletingChatId(chatId);
    setChatMenuOpenId(null);
    try {
      await deleteChat(chatId);
      removeChatFromLocalPreferences(chatId);
      if (String(selectedChatId || "") === String(chatId)) onChatSelect(null);
      toast.success("Chat deleted.");
    } catch (err) {
      console.error(err);
      toast.error("Could not delete this chat. Check your connection or permissions.");
    } finally {
      setDeletingChatId(null);
    }
  };

  const archiveChatForCurrentUser = (chatId) => {
    setArchivedChatIds((prev) => {
      const next = prev.includes(chatId) ? prev : [...prev, chatId];
      try {
        localStorage.setItem(ARCHIVED_CHATS_KEY(currentUserId), JSON.stringify(next));
      } catch (e) {
        console.error("Failed to save archived chats", e);
      }
      return next;
    });
    setChatMenuOpenId(null);
    if (selectedChatId === chatId) onChatSelect(null);
    toast.success("Chat archived.");
  };

  const unarchiveChatForCurrentUser = (chatId) => {
    setArchivedChatIds((prev) => {
      const next = prev.filter((id) => id !== chatId);
      try {
        localStorage.setItem(ARCHIVED_CHATS_KEY(currentUserId), JSON.stringify(next));
      } catch (e) {
        console.error("Failed to save archived chats", e);
      }
      return next;
    });
    setChatMenuOpenId(null);
    toast.success("Chat moved back to your inbox.");
  };

  useEffect(() => {
    if (!chatMenuOpenId) return;
    const onDocClick = (e) => {
      if (e.target.closest("[data-chat-menu]")) return;
      setChatMenuOpenId(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [chatMenuOpenId]);

  // Fetch member information for a user_id
  const fetchMemberInfo = async (userId) => {
    if (!userId) return null;
    if (memberInfoMap[userId]) {
      return memberInfoMap[userId];
    }

    try {
      const familyTreeId = getFamilyTreeIdFromStorage();

      if (!familyTreeId) {
        console.log("No familyTreeId found for fetching member info");
        return null;
      }

      console.log(`Fetching member info for userId: ${userId}`);
      const response = await getApi(
        `/family-members-for-invitation?family_tree_id=${familyTreeId}`
      );

      if (response?.statusCode === 200 && response?.data?.members) {
        const member = response.data.members.find(
          (m) => m.user_id === userId
        );
        if (member) {
          console.log(`Found member info for ${userId}:`, member.first_name || member.name, "Color:", member.color_name);
          setMemberInfoMap((prev) => ({
            ...prev,
            [userId]: member,
          }));
          return member;
        } else {
          console.log(`Member not found in API response for userId: ${userId}`);
          console.log("Available user_ids:", response.data.members.map(m => m.user_id));
          console.log("This might mean the user is not in the same family tree or the API doesn't return all members");
        }
      } else {
        console.log("API response status:", response?.statusCode);
      }
    } catch (error) {
      console.error(`Error fetching member info for userId ${userId}:`, error);
    }
    return null;
  };

  // Fetch member info for all participants in chats
  useEffect(() => {
    const fetchAllMemberInfo = async () => {
      if (chats.length === 0 || !currentUserId) return;

      const userIds = new Set();
      chats.forEach((chat) => {
        if (chat.participants && Array.isArray(chat.participants)) {
          chat.participants.forEach((userId) => {
            if (userId && userId !== currentUserId) {
              userIds.add(userId);
            }
          });
        }
      });

      // Fetch for all userIds (fetchMemberInfo checks cache internally)
      const userIdsArray = Array.from(userIds);
      if (userIdsArray.length > 0) {
        console.log("Fetching member info for userIds:", userIdsArray);
        // Fetch all member info in parallel
        const fetchPromises = userIdsArray.map((userId) =>
          fetchMemberInfo(userId).catch((err) => {
            console.error(`Failed to fetch member info for ${userId}:`, err);
            return null;
          })
        );
        await Promise.all(fetchPromises);
      }

      // Also extract names from lastMessage for chats where member info isn't available yet
      // This is a fallback while member info is loading
      const namesFromMessages = {};
      chats.forEach((chat) => {
        if (chat.type === "individual" && chat.lastMessage?.senderName) {
          const otherUserId = getOtherParticipantId(chat, currentUserId);
          if (otherUserId && chat.lastMessage.senderId === otherUserId) {
            namesFromMessages[otherUserId] = chat.lastMessage.senderName;
          }
        }
      });
      
      if (Object.keys(namesFromMessages).length > 0) {
        setParticipantNamesFromMessages((prev) => ({
          ...prev,
          ...namesFromMessages,
        }));
      }
    };

    fetchAllMemberInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats, currentUserId]); // memberInfoMap intentionally excluded to avoid infinite loop

  // Backfill isAdminSupport for existing Admin Support chats (user side): so they show under Support Request for admin
  useEffect(() => {
    let isAdmin = false;
    try {
      const role = JSON.parse(localStorage.getItem("loggedIn_user_role") || "{}");
      isAdmin = role?.is_admin === true;
    } catch {
      // ignore
    }
    if (isAdmin || chats.length === 0) return;

    const adminId = getAdminUserIdFromDetails(getAdminDetailsFromStorage());
    if (!adminId) return;

    chats.forEach((chat) => {
      if (chat.type !== "individual" || chat.isAdminSupport) return;
      const otherUserId = getOtherParticipantId(chat, currentUserId);
      if (otherUserId === adminId) {
        setChatAsAdminSupport(chat.id).catch((err) =>
          console.error("Backfill isAdminSupport failed:", err)
        );
      }
    });
  }, [chats, currentUserId]);

  // Fetch senderName from messages for chats where lastMessage doesn't have it
  useEffect(() => {
    const fetchMissingSenderNames = async () => {
      if (chats.length === 0 || !currentUserId) return;

      const chatsToFetch = chats.filter((chat) => {
        if (chat.type !== "individual") return false;
        if (!chat.id) return false;
        if (fetchingMessageNames.has(chat.id)) return false;
        
        const otherUserId = getOtherParticipantId(chat, currentUserId);
        if (!otherUserId) return false;
        
        // Only fetch if we don't have memberInfo and lastMessage doesn't have senderName
        const hasMemberInfo = memberInfoMap[otherUserId];
        const hasSenderName = chat.lastMessage?.senderName;
        const hasCachedName = participantNamesFromMessages[otherUserId];
        
        return !hasMemberInfo && !hasSenderName && !hasCachedName;
      });

      for (const chat of chatsToFetch) {
        const otherUserId = getOtherParticipantId(chat, currentUserId);
        if (!otherUserId) continue;

        setFetchingMessageNames((prev) => new Set(prev).add(chat.id));

        try {
          const messagesRef = collection(db, "chats", chat.id, "messages");
          const q = query(messagesRef, orderBy("timestamp", "desc"), limit(10));
          const snapshot = await getDocs(q);
          
          const messages = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          
          // Find a message from the other participant
          const otherParticipantMessage = messages.find(
            (msg) => msg.senderId === otherUserId && msg.senderName
          );
          
          if (otherParticipantMessage?.senderName) {
            setParticipantNamesFromMessages((prev) => ({
              ...prev,
              [otherUserId]: otherParticipantMessage.senderName,
            }));
          }
        } catch (err) {
          console.error("Error fetching messages for senderName:", err);
        } finally {
          setFetchingMessageNames((prev) => {
            const newSet = new Set(prev);
            newSet.delete(chat.id);
            return newSet;
          });
        }
      }
    };

    fetchMissingSenderNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats, currentUserId, memberInfoMap, participantNamesFromMessages]);

  // Format participant name for display
  const getParticipantName = (chat) => {
    if (chat.type === "group") {
      return chat.groupName || "Group Chat";
    }

    // Individual chat - get other participant
    const otherUserId = getOtherParticipantId(chat, currentUserId);
    if (!otherUserId) return "Unknown";

    // Admin Support chat: user sees "Admin Support (AdminName)", admin sees just the user's name
    try {
      const adminDetails = getAdminDetailsFromStorage();
      const adminId = getAdminUserIdFromDetails(adminDetails);
      const otherIsAdmin = adminId && String(adminId) === String(otherUserId);
      const isAdminSupportChat = otherIsAdmin || chat.isAdminSupport === true;
      if (isAdminSupportChat) {
        if (otherIsAdmin) {
          // Other participant is the admin → we're the user → show just "Admin Support"
          return "Admin Support";
        }
        // Other participant is the user who requested support → we're the admin → show just user's name
        const userInfo = memberInfoMap[otherUserId] || chat.participantInfo?.[otherUserId];
        if (userInfo) {
          const firstName = userInfo.first_name || userInfo.name || "";
          const suffix = userInfo.suffix || "";
          const specialName = userInfo.special_name || "";
          if (suffix) {
            const suffixUpper = suffix.trim().toUpperCase();
            const suffixWithPeriod =
              suffixUpper === "SR" || suffixUpper === "JR" ? `${suffix.trim()}.` : suffix.trim();
            return `${firstName}, ${suffixWithPeriod}`;
          }
          if (specialName) return `${firstName} ${specialName}`.trim();
          return (firstName || userInfo.name || "User").trim() || "User";
        }
        // Fall through to use lastMessage.senderName / participantNamesFromMessages if needed
      }
    } catch {
      // ignore
    }

    // First, try to get from memberInfoMap (from API)
    let memberInfo = memberInfoMap[otherUserId];
    
    // If not found, try to get from chat.participantInfo (stored in chat document)
    if (!memberInfo && chat.participantInfo && chat.participantInfo[otherUserId]) {
      memberInfo = chat.participantInfo[otherUserId];
    }
    
    if (memberInfo) {
      const firstName = memberInfo.first_name || "";
      const suffix = memberInfo.suffix || "";
      const specialName = memberInfo.special_name || "";

      if (suffix) {
        const suffixUpper = suffix.trim().toUpperCase();
        const suffixWithPeriod =
          suffixUpper === "SR" || suffixUpper === "JR"
            ? `${suffix.trim()}.`
            : suffix.trim();
        return `${firstName}, ${suffixWithPeriod}`;
      } else if (specialName) {
        return `${firstName} ${specialName}`;
      }
      return firstName || memberInfo.name || "Unknown";
    }

    // If member info is not loaded yet, try fallbacks:
    // 1. Use senderName from lastMessage if it was sent by the other participant
    if (chat.lastMessage?.senderName && chat.lastMessage?.senderId === otherUserId) {
      console.log(`Using lastMessage.senderName for ${otherUserId}:`, chat.lastMessage.senderName);
      return chat.lastMessage.senderName;
    }

    // 2. Check if we have the name cached from previous messages
    if (participantNamesFromMessages[otherUserId]) {
      console.log(`Using cached name for ${otherUserId}:`, participantNamesFromMessages[otherUserId]);
      return participantNamesFromMessages[otherUserId];
    }

    // Show loading state - member info should load soon via useEffect
    // The useEffect will fetch senderName from messages if needed
    return "Loading...";
  };

  const getHeadOfLineName = (member) => {
    if (!member) return "";
    const name = formatDashboardFamilyLineHeadName(member);
    return name || "Start/Top of Line";
  };

  // Get participant color/line info. Hide only when user is viewing admin (Admin Support); show when admin is viewing user.
  const getParticipantColor = (chat) => {
    if (chat.type === "group") return null;

    const otherUserId = getOtherParticipantId(chat, currentUserId);
    if (!otherUserId) return null;

    // Don't show color line only when the other participant is the admin (user side viewing Admin Support)
    try {
      const adminDetails = getAdminDetailsFromStorage();
      const adminId = getAdminUserIdFromDetails(adminDetails);
      if (adminId && String(adminId) === String(otherUserId)) return null;
    } catch {
      // ignore
    }

    // First, try to get from memberInfoMap (from API)
    let memberInfo = memberInfoMap[otherUserId];
    
    // If not found, try to get from chat.participantInfo (stored in chat document)
    if (!memberInfo && chat.participantInfo && chat.participantInfo[otherUserId]) {
      memberInfo = chat.participantInfo[otherUserId];
    }
    
    if (!memberInfo) {
      // Return null if no member info yet - it will be fetched by useEffect
      return null;
    }
    
    const headOfLineName = getHeadOfLineName(memberInfo);
    const colorName = memberInfo?.color_name || "";
    const hexCode = memberInfo?.hex_code || "#000000";
    
    // Only return if we have color info
    if (!colorName || !hexCode || hexCode === "#000000") {
      return null;
    }
    
    return {
      hexCode: hexCode,
      colorName: colorName,
      headOfLineName: headOfLineName || "Start/Top of Line",
    };
  };

  // Admin sees Support Request tab (from localStorage)
  const isAdmin = (() => {
    try {
      const role = JSON.parse(localStorage.getItem("loggedIn_user_role") || "{}");
      return role?.is_admin === true;
    } catch {
      return false;
    }
  })();
  const tabs = isAdmin
    ? ["All", "Individual", "Group", "Support Request", "Archived", "Blocked"]
    : ["All", "Individual", "Group", "Archived", "Blocked"];

  const iBlockedOtherInChat = (chat) => {
    if (!chat || !currentUserId) return false;
    const isOneToOne =
      chat.type === "individual" ||
      (chat.type !== "group" && (chat.participants || []).length === 2);
    if (!isOneToOne) return false;
    const me = resolveMyParticipantIdInChat(chat, currentUserId);
    const other = getOtherParticipantId(chat, me);
    if (!other) return false;
    const blocks = getIndividualChatBlocksMap(chat);
    const key = Object.keys(blocks).find((k) => String(k) === String(other));
    return key != null && String(blocks[key]) === String(me);
  };

  // Filter chats based on search and tab
  const filteredChats = chats.filter((chat) => {
    // Hide chats that the current user has removed from their list
    if (hiddenChatIds.some((hid) => String(hid) === String(chat.id))) return false;

    const isArchived = archivedChatIds.some((aid) => String(aid) === String(chat.id));

    if (activeTab === "Archived") {
      if (!isArchived) return false;
    } else if (isArchived) {
      return false;
    }

    // Tab filter
    if (activeTab === "Group" && chat.type !== "group") return false;
    if (activeTab === "Individual" && chat.type !== "individual") return false;
    // Support chats live only under Support Request for the admin, not Individual
    if (activeTab === "Individual" && isAdmin && chat.isAdminSupport === true) return false;
    if (activeTab === "Support Request" && chat.isAdminSupport !== true) return false;
    if (activeTab === "Blocked") {
      if (chat.type === "group") return false;
      return iBlockedOtherInChat(chat);
    }
    if (activeTab === "Unread") {
      // You can add unread logic here later
      return true;
    }

    // Search filter
    if (searchQuery.trim()) {
      const name = getParticipantName(chat).toLowerCase();
      const lastMessage = chat.lastMessage?.text?.toLowerCase() || "";
      return (
        name.includes(searchQuery.toLowerCase()) ||
        lastMessage.includes(searchQuery.toLowerCase())
      );
    }

    return true;
  });

  const CHAT_LIST_LIMIT = 50;
  const totalFilteredCount = filteredChats.length;
  const visibleChats = showFullList ? filteredChats : filteredChats.slice(0, CHAT_LIST_LIMIT);
  const hasMoreChats = totalFilteredCount > CHAT_LIST_LIMIT;

  // Get last message preview
  const getLastMessagePreview = (chat) => {
    if (chat.lastMessage?.text) {
      const text = stripMessageMarkup(chat.lastMessage.text);
      return text.length > 40 ? text.substring(0, 40) + "..." : text;
    }
    return "No messages yet";
  };

  // Format time for display
  const getTimeDisplay = (chat) => {
    if (chat.lastMessageAt) {
      return formatMessageTime(chat.lastMessageAt);
    }
    if (chat.updatedAt) {
      return formatMessageTime(chat.updatedAt);
    }
    return "";
  };

  if (chatsLoading) {
    return (
      <div
        style={{
          width: "100%",
          padding: "20px",
          textAlign: "center",
          color: "#666",
        }}
      >
        Loading chats...
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Header - Removed since it's already in the page header */}

      {/* Search and New Chat */}
      <div
        style={{
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          borderBottom: "1px solid #e8e8e8",
        }}
      >
        {onCollapseChatList && (
          <button
            type="button"
            className="messages-chat-list-collapse-btn"
            onClick={onCollapseChatList}
            title="Hide left side"
            aria-expanded="true"
            aria-controls="messages-chat-list-panel"
          >
            <FontAwesomeIcon icon={faAngleDoubleLeft} style={{ color: "#fff" }} />
          </button>
        )}
        <div
          style={{
            flex: 1,
            position: "relative",
            display: "flex",
            alignItems: "center",
            minWidth: 0,
          }}
        >
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: searchQuery ? "8px 32px 8px 36px" : "8px 12px 8px 36px",
              border: "1px solid #D9D9D9",
              borderRadius: "4px",
              fontSize: "16px",
              outline: "none",
            }}
          />
          <span
            style={{
              position: "absolute",
              left: "12px",
              color: "#999",
            }}
          >
            🔍
          </span>
          {searchQuery && (
            <span
              onClick={() => setSearchQuery("")}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: "#555",
                fontSize: "16px",
                lineHeight: 1,
                fontWeight: "bold",
                userSelect: "none",
              }}
            >
              ×
            </span>
          )}
        </div>
        <div
          style={{
            position: "relative",
          }}
          onMouseEnter={() => setShowNewChatTooltip(true)}
          onMouseLeave={() => setShowNewChatTooltip(false)}
        >
          <button
            onClick={() => {
              // Trigger new chat modal - you can pass a callback
              if (window.openNewChatModal) {
                window.openNewChatModal();
              }
            }}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              backgroundColor: "#025FEB",
              border: "none",
              color: "#fff",
              fontSize: "20px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            +
          </button>
          {showNewChatTooltip && (
            <div
              style={{
                position: "absolute",
                top: "50px",
                right: "0",
                backgroundColor: "#fff",
                color: "#000",
                padding: "8px 12px",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                minWidth: "250px",
                border: "1px solid #D9D9D9",
                zIndex: 1000,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                pointerEvents: "none",
              }}
            >
              Click to start a new individual or group chat
              <div
                style={{
                  position: "absolute",
                  top: "-5px",
                  right: "12px",
                  width: "0",
                  height: "0",
                  borderLeft: "5px solid transparent",
                  borderRight: "5px solid transparent",
                  borderBottom: "5px solid #D9D9D9",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "12px",
                  width: "0",
                  height: "0",
                  borderLeft: "5px solid transparent",
                  borderRight: "5px solid transparent",
                  borderBottom: "5px solid #fff",
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "stretch",
          gap: "5px",
          borderBottom: "1px solid #e8e8e8",
          padding: "0 10px",
          overflowX: "auto",
          flexShrink: 0,
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            className="messages-chat-list-tab-button"
            onClick={() => setActiveTab(tab)}
            style={{
              padding: "10px 16px",
              border: "none",
              backgroundColor: "transparent",
              color: activeTab === tab ? "#025FEB" : "#666",
              borderBottom:
                activeTab === tab ? "2px solid #025FEB" : "2px solid transparent",
              cursor: "pointer",
              fontSize: "15px",
              fontWeight: "600",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Chat List */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {totalFilteredCount > 0 && (
          <div
            style={{
              padding: "8px 16px",
              borderBottom: "1px solid #eee",
              fontSize: "13px",
              color: "#666",
              flexShrink: 0,
            }}
          >
            Showing {visibleChats.length} of {totalFilteredCount}
            {hasMoreChats && (
              <button
                type="button"
                onClick={() => setShowFullList((prev) => !prev)}
                style={{
                  marginLeft: "8px",
                  padding: "2px 8px",
                  border: "1px solid #025FEB",
                  borderRadius: "4px",
                  background: "#fff",
                  color: "#025FEB",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                {showFullList ? "Show less" : "Show list"}
              </button>
            )}
          </div>
        )}
        {filteredChats.length === 0 ? (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: "#999",
              fontSize: "16px",
            }}
          >
            {searchQuery
              ? "No chats found"
              : activeTab === "Blocked"
                ? "No blocked users"
                : "No chats available"}
          </div>
        ) : (
          visibleChats.map((chat) => {
            const isSelected = selectedChatId === chat.id;
            const participantName = getParticipantName(chat);
            const participantColor = getParticipantColor(chat);
            const lastMessage = getLastMessagePreview(chat);
            const timeDisplay = getTimeDisplay(chat);

            return (
              <div
                key={chat.id}
                onClick={() => {
                  setChatMenuOpenId(null);
                  onChatSelect(chat);
                }}
                style={{
                  padding: "8px 16px",
                  cursor: "pointer",
                  backgroundColor: isSelected ? "#f0f7ff" : "#fff",
                  borderLeft: isSelected ? "3px solid #025FEB" : "3px solid transparent",
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = "#f9f9f9";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = "#fff";
                }}
              >
                {/* Chat Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "2px",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          flexWrap: "wrap",
                          minWidth: 0,
                          rowGap: "2px",
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "15px",
                            lineHeight: 1.25,
                          }}
                          title={
                            participantColor &&
                            participantColor.colorName &&
                            participantColor.hexCode
                              ? `${participantName} • ${participantColor.colorName} Line`
                              : participantName
                          }
                        >
                          <span
                            style={{
                              fontWeight: "600",
                              color: "#333",
                            }}
                          >
                            {participantName}
                          </span>
                          {participantColor &&
                            participantColor.colorName &&
                            participantColor.hexCode && (
                              <span
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "400",
                                  marginLeft: "6px",
                                }}
                              >
                                {renderColorDotAndName(
                                  participantColor.hexCode,
                                  `${participantColor.colorName} Line`
                                )}
                              </span>
                            )}
                        </div>
                        {iBlockedOtherInChat(chat) && (
                          <span
                            style={{
                              fontSize: "10px",
                              fontWeight: "700",
                              color: "#fff",
                              backgroundColor: "#c62828",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              letterSpacing: "0.03em",
                              flexShrink: 0,
                            }}
                          >
                            BLOCKED
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      data-chat-menu
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        flexShrink: 0,
                        marginLeft: "8px",
                        position: "relative",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {timeDisplay && (
                        <span
                          style={{
                            fontSize: "14px",
                            color: "#999",
                          }}
                        >
                          {timeDisplay}
                        </span>
                      )}
                      <button
                        type="button"
                        aria-label="Chat options"
                        onClick={(e) => {
                          e.stopPropagation();
                          setChatMenuOpenId((prev) => (prev === chat.id ? null : chat.id));
                        }}
                        style={{
                          padding: "4px",
                          border: "none",
                          background: "transparent",
                          borderRadius: "4px",
                          cursor: "pointer",
                          color: "#666",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#eee";
                          e.currentTarget.style.color = "#333";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.color = "#666";
                        }}
                      >
                        ⋮
                      </button>
                      {chatMenuOpenId === chat.id && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            right: 0,
                            marginTop: "2px",
                            backgroundColor: "#fff",
                            border: "1px solid #e8e8e8",
                            borderRadius: "6px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                            zIndex: 100,
                            minWidth: "140px",
                            overflow: "hidden",
                          }}
                        >
                          {activeTab === "Blocked" && iBlockedOtherInChat(chat) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnblockFromList(chat);
                              }}
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                border: "none",
                                background: "none",
                                cursor: "pointer",
                                fontSize: "14px",
                                color: "#2e7d32",
                                textAlign: "left",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#e8f5e9";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                              }}
                            >
                              Unblock
                            </button>
                          )}
                          {activeTab === "Archived" ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                unarchiveChatForCurrentUser(chat.id);
                              }}
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                border: "none",
                                background: "none",
                                cursor: "pointer",
                                fontSize: "14px",
                                color: "#333",
                                textAlign: "left",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#f5f5f5";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                              }}
                            >
                              Unarchive chat
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                archiveChatForCurrentUser(chat.id);
                              }}
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                border: "none",
                                background: "none",
                                cursor: "pointer",
                                fontSize: "14px",
                                color: "#333",
                                textAlign: "left",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#f5f5f5";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                              }}
                            >
                              Archive Chat
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={deletingChatId === chat.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              permanentlyDeleteChat(chat.id);
                            }}
                            style={{
                              width: "100%",
                              padding: "10px 12px",
                              border: "none",
                              background: "none",
                              cursor: deletingChatId === chat.id ? "wait" : "pointer",
                              fontSize: "14px",
                              color: "#c00",
                              textAlign: "left",
                              opacity: deletingChatId === chat.id ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#fff5f5";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }}
                          >
                            {deletingChatId === chat.id ? "Deleting…" : "Delete chat"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      color: "#666",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {lastMessage}
                  </div>
                </div>

                {/* Read indicator */}
                {/* <div
                  style={{
                    width: "16px",
                    height: "16px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ✓✓
                </div> */}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ChatList;
